import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, IconButton, Typography, Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useLocation } from "react-router-dom";
import { UserSettingsContext } from "../../../setup/routes-manager/RequireAuth";
import { FREE_TOOL_STEEL_CARDS, getSteelCardInitialActiveId } from "./free_tool_steel_card_config";
import { useWindowSize } from "../../../common/hooks/useWindowSize";

const TABLET_BREAKPOINT = 640;
const LAPTOP_BREAKPOINT = 1024;

/** MP reset-zoom FAB sits bottom-centre — lift card above it. */
const MP_FAB_CLEARANCE = 150;

const getShellSx = (toolKey, viewportWidth) => {
    const isMobile =
        viewportWidth !== undefined && viewportWidth < TABLET_BREAKPOINT;
    const isCompact =
        viewportWidth !== undefined && viewportWidth < LAPTOP_BREAKPOINT;
    const isMp = toolKey === "mp";
    const isChartTool = toolKey === "iv" || toolKey === "oi" || toolKey === "vol";

    const base = {
        position: "fixed !important",
        left: "auto !important",
        top: "auto !important",
        zIndex: "25000 !important",
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
        pointerEvents: "auto",
    };

    if (isMobile) {
        return {
            ...base,
            right: "12px !important",
            bottom: "12px !important",
            width: "calc(100vw - 24px)",
        };
    }

    if (isCompact) {
        const bottom = isMp
            ? `${MP_FAB_CLEARANCE}px !important`
            : isChartTool
              ? "72px !important"
              : "56px !important";

        return {
            ...base,
            right: "12px !important",
            bottom,
            width: "min(300px, calc(100vw - 24px))",
        };
    }

    const bottom = isMp
        ? `${MP_FAB_CLEARANCE}px !important`
        : "64px !important";

    return {
        ...base,
        right: "20px !important",
        bottom,
        width: "min(340px, calc(100vw - 24px))",
    };
};

/**
 * Bottom-right steel card nudge for free tool views.
 */
export default function FreeToolSteelCard({
    toolKey,
    title,
    subtitle,
    description,
    ctaLabel,
}) {
    const config = toolKey ? FREE_TOOL_STEEL_CARDS[toolKey] : null;
    const { width: viewportWidth } = useWindowSize();
    const { pathname } = useLocation();

    const [dismissed, setDismissed] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!toolKey) {
            setDismissed(true);
            setReady(false);
            return;
        }

        setDismissed(false);
        setCollapsed(false);
        const timer = window.setTimeout(() => setReady(true), 400);
        return () => window.clearTimeout(timer);
    }, [toolKey, pathname]);

    const { openToolsWalkthrough } = useContext(UserSettingsContext);

    const card = useMemo(
        () => ({
            title: title ?? config?.title,
            subtitle: subtitle ?? config?.subtitle,
            description: description ?? config?.description,
            ctaLabel: ctaLabel ?? config?.ctaLabel,
            initialActiveId: getSteelCardInitialActiveId(toolKey),
        }),
        [
            title,
            subtitle,
            description,
            ctaLabel,
            toolKey,
            config,
        ],
    );

    const shellSx = useMemo(
        () => getShellSx(toolKey, viewportWidth),
        [toolKey, viewportWidth],
    );

    const isCompact =
        viewportWidth !== undefined && viewportWidth < LAPTOP_BREAKPOINT;

    const handleDismiss = () => {
        setDismissed(true);
    };

    const handleCta = () => {
        openToolsWalkthrough?.(card.initialActiveId);
        handleDismiss();
    };

    if (!toolKey || dismissed || !card.title || !ready) {
        return null;
    }

    if (typeof document === "undefined" || !document.body) {
        return null;
    }

    return createPortal(
        <Box sx={shellSx}>
            <Box
                sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid rgba(180, 190, 210, 0.65)",
                    boxShadow:
                        "0 12px 40px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255,255,255,0.5) inset",
                    background: `
                        linear-gradient(145deg,
                            #e8edf5 0%,
                            #d4dce8 22%,
                            #c5cedc 45%,
                            #dde3ec 70%,
                            #eef2f8 100%
                        )
                    `,
                }}
            >
                <Box
                    sx={{
                        px: { mobile: 1.5, tablet: 2 },
                        py: { mobile: 1, tablet: 1.25 },
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        borderBottom: collapsed
                            ? "none"
                            : "1px solid rgba(120, 135, 160, 0.35)",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(200,210,225,0.2) 100%)",
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: {
                                    mobile: "0.88rem",
                                    tablet: "0.95rem",
                                },
                                fontWeight: 700,
                                color: "#1a2d52",
                                lineHeight: 1.3,
                            }}
                        >
                            {card.title}
                        </Typography>
                        {!collapsed && (
                            <Typography
                                sx={{
                                    fontSize: {
                                        mobile: "0.75rem",
                                        tablet: "0.8rem",
                                    },
                                    fontWeight: 600,
                                    color: "#3f69f8",
                                    mt: 0.25,
                                }}
                            >
                                {card.subtitle}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            flexShrink: 0,
                        }}
                    >
                        <IconButton
                            size="small"
                            aria-label={
                                collapsed ? "Expand card" : "Collapse card"
                            }
                            onClick={() => setCollapsed((prev) => !prev)}
                            sx={{
                                color: "#5a6b85",
                                "&:hover": {
                                    color: "#1a2d52",
                                    backgroundColor: "rgba(255,255,255,0.45)",
                                },
                            }}
                        >
                            {collapsed ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                        <IconButton
                            size="small"
                            aria-label="Close"
                            onClick={handleDismiss}
                            sx={{
                                color: "#5a6b85",
                                "&:hover": {
                                    color: "#1a2d52",
                                    backgroundColor: "rgba(255,255,255,0.45)",
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={!collapsed}>
                    <Box sx={{
                            px: { mobile: 1.5, tablet: 2 },
                            pt: 1.5,
                            pb: isCompact ? 1.25 : 1.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: {
                                    mobile: "0.8rem",
                                    tablet: "0.84rem",
                                },
                                lineHeight: 1.6,
                                color: "#3d4f6a",
                                mb: isCompact ? 1.25 : 2,
                            }}
                        >
                            {card.description}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={handleCta}
                            fullWidth
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: {
                                    mobile: "0.8rem",
                                    tablet: "0.84rem",
                                },
                                borderRadius: "8px",
                                py: 0.85,
                                px: 2,
                                backgroundColor: "#3f69f8",
                                boxShadow:
                                    "0 4px 12px rgba(63, 105, 248, 0.35)",
                                "&:hover": {
                                    backgroundColor: "#355de0",
                                },
                            }}
                        >
                            {card.ctaLabel}
                        </Button>
                    </Box>
                </Collapse>
            </Box>
        </Box>,
        document.body,
    );
}
